'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';
import AppLogo from '@/components/ui/AppLogo';

const steps = [
  {
    number: '01',
    phase: 'Send',
    title: 'Post Your Delivery Order',
    color: 'from-[#0D9488] to-[#0F766E]',
    accent: '#0D9488',
    lightBg: 'bg-teal-50',
    border: 'border-teal-200',
    icon: '📦',
    who: 'Sender',
    time: '~3 minutes',
    description: 'Describe your parcel, set your route, and name your price. Your order goes live on the marketplace instantly.',
    steps: [
      'Go to Create Order and fill in pickup & destination (e.g. Nairobi → Mombasa)',
      'Describe your parcel: category, weight, dimensions, declared value',
      'Upload photos of the parcel for traveler verification',
      'Set your offered price (e.g. KES 350 for a 2kg electronics parcel)',
      'Set a delivery deadline — travelers see orders expiring soonest first',
    ],
    example: {
      label: 'Real Example',
      text: 'Amina in Nairobi posts: "2kg laptop bag, Nairobi CBD → Mombasa Town, KES 400, deadline Friday 6pm." Her order appears on the marketplace in seconds.',
    },
    tip: 'Orders with photos and clear descriptions get 3× more bids.',
  },
  {
    number: '02',
    phase: 'Bid',
    title: 'Travelers Compete for Your Order',
    color: 'from-[#2563EB] to-[#1D4ED8]',
    accent: '#2563EB',
    lightBg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: '🏷️',
    who: 'Traveler',
    time: 'Minutes to hours',
    description: 'Verified travelers on matching routes browse the marketplace and place competitive bids. You see their ratings, guarantors, and trip details.',
    steps: [
      'Traveler browses Marketplace and filters by route, weight, and transport method',
      'Traveler views your parcel details, photos, and offered price',
      'Traveler places a bid — they can match your price or negotiate',
      'You receive a notification: "New bid from @john_maina (★4.9, 47 deliveries)"',
      'You can chat with the traveler before accepting to ask questions',
    ],
    example: {
      label: 'Real Example',
      text: 'John Maina (Nairobi → Mombasa, Train, departing Thursday 8am) bids KES 380 on Amina\'s order. He has a verified guarantor and 47 completed deliveries.',
    },
    tip: 'Travelers with guarantors and high ratings are your safest choice.',
  },
  {
    number: '03',
    phase: 'Accept',
    title: 'Accept the Best Bid',
    color: 'from-[#16A34A] to-[#15803D]',
    accent: '#16A34A',
    lightBg: 'bg-green-50',
    border: 'border-green-200',
    icon: '✅',
    who: 'Sender',
    time: '~1 minute',
    description: 'Review bids, compare travelers, and accept the one you trust most. All other bids are automatically declined.',
    steps: [
      'Open My Orders → View Bids on your order',
      'Compare bids: price, traveler rating, guarantor status, departure time',
      'Accept the bid that best fits your needs',
      'All other pending bids are automatically rejected',
      'A delivery record is created — both parties are notified immediately',
    ],
    example: {
      label: 'Real Example',
      text: 'Amina accepts John\'s bid of KES 380. John gets a notification: "Your bid was accepted — check Active Deliveries." All 3 other bidders are notified their bids were declined.',
    },
    tip: 'You can message travelers before accepting — ask about their exact departure time or vehicle.',
  },
  {
    number: '04',
    phase: 'Pack & Pickup',
    title: 'Pack Together & Confirm Pickup',
    color: 'from-[#D97706] to-[#B45309]',
    accent: '#D97706',
    lightBg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: '🤝',
    who: 'Both',
    time: 'At pickup location',
    description: 'Sender and traveler meet to pack the parcel together. This joint verification ensures only legal items are carried — protecting both parties.',
    steps: [
      'Sender confirms "Package is Packed & Ready" in Active Deliveries',
      'Traveler meets sender at pickup location',
      'Both parties inspect and pack the parcel together',
      'Traveler takes a pickup photo for verification',
      'Traveler confirms pickup via QR code + GPS — delivery officially starts',
    ],
    example: {
      label: 'Real Example',
      text: 'John meets Amina at Nairobi CBD. They pack the laptop bag together. John photographs the sealed parcel, then taps "Confirm Pickup (QR + GPS)" — his GPS coordinates are recorded.',
    },
    tip: 'The joint packing step is MuyuRide\'s #1 fraud prevention mechanism. Never skip it.',
  },
  {
    number: '05',
    phase: 'Deliver',
    title: 'Live GPS Tracking in Transit',
    color: 'from-[#7C3AED] to-[#6D28D9]',
    accent: '#7C3AED',
    lightBg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: '🚌',
    who: 'Traveler',
    time: 'Duration of journey',
    description: 'The traveler starts the journey. Sender tracks progress live. Traveler shares GPS updates along the route.',
    steps: [
      'Traveler taps "Start Journey" — order status changes to In Transit',
      'Sender sees live progress bar and GPS breadcrumbs on the map',
      'Traveler shares location updates at checkpoints (e.g. "Now at Mtito Andei")',
      'Both parties can message each other via Travel Chat at any time',
      'On arrival, traveler taps "Mark as Delivered" — sender is notified',
    ],
    example: {
      label: 'Real Example',
      text: 'John\'s train departs Nairobi at 8am. Amina watches the progress bar move from 0% to 100% over 8 hours. At 4pm, she gets a notification: "Your traveler has arrived at Mombasa Town."',
    },
    tip: 'Enable location sharing for the best tracking experience. Travelers who share GPS get higher trust scores.',
  },
  {
    number: '06',
    phase: 'Pay & Review',
    title: 'Confirm Receipt & Release Payment',
    color: 'from-[#059669] to-[#047857]',
    accent: '#059669',
    lightBg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: '💰',
    who: 'Sender',
    time: '~2 minutes',
    description: 'Sender confirms receipt using a PIN or digital signature. Payment is instantly released from escrow to the traveler\'s M-Pesa. Both parties leave reviews.',
    steps: [
      'Traveler hands over the parcel at destination',
      'Sender opens Active Deliveries → "Confirm Receipt & Release Payment"',
      'Sender verifies using delivery PIN or digital signature',
      'KES payment is released from escrow to traveler\'s M-Pesa instantly',
      'Both parties receive a review prompt — ratings build community trust',
    ],
    example: {
      label: 'Real Example',
      text: 'Amina\'s recipient in Mombasa confirms receipt. Amina taps "Confirm Receipt" and enters the 6-digit PIN. John receives KES 361 (KES 380 minus 5% platform fee) to his M-Pesa within seconds. Ref: MPE73801822.',
    },
    tip: 'Payment is held in escrow until you confirm receipt — your money is always protected.',
  },
];

const trustFeatures = [
  { icon: '🛡️', title: 'Guarantor Verification', desc: 'Every traveler has a verified guarantor on file — a real person who vouches for them.' },
  { icon: '🔐', title: 'ID Verification', desc: 'National ID and phone number verified for all travelers before their first delivery.' },
  { icon: '🔒', title: 'M-Pesa Escrow', desc: 'Payment is held securely until you confirm receipt. No delivery = no payment.' },
  { icon: '📍', title: 'Live GPS Tracking', desc: 'Real-time breadcrumb trail from pickup to delivery. You always know where your parcel is.' },
  { icon: '⭐', title: 'Verified Reviews', desc: 'Only completed deliveries generate reviews. No fake ratings — ever.' },
  { icon: '🤝', title: 'Joint Packing', desc: 'Sender and traveler pack together, preventing fraud and ensuring only legal items are carried.' },
];

const faqs = [
  { q: 'What happens if my parcel is lost or damaged?', a: 'MuyuRide\'s escrow system means payment is never released until you confirm receipt. If there\'s a dispute, our team reviews evidence from both parties. Travelers with guarantors are financially accountable.' },
  { q: 'How long does delivery take?', a: 'It depends on the route and transport method. Nairobi → Mombasa by train takes ~8 hours. Nairobi → Kisumu by bus takes ~5 hours. You set the deadline when posting your order.' },
  { q: 'Can I track my parcel in real time?', a: 'Yes. Once the traveler starts the journey, you can see live GPS updates, a progress bar, and checkpoint messages in Active Deliveries.' },
  { q: 'What if no traveler bids on my order?', a: 'Orders expire after 3 hours if no bid is accepted. You\'ll be notified and can repost with a higher price or adjusted deadline. Popular routes like Nairobi → Mombasa typically get bids within minutes.' },
  { q: 'How is the traveler paid?', a: 'Payment is released from M-Pesa escrow to the traveler\'s M-Pesa within seconds of you confirming receipt. MuyuRide takes a 5% platform fee; the traveler receives 95%.' },
  { q: 'What items can I send?', a: 'Documents, electronics, clothing, food, medicine, household items, books, and more. Illegal items, weapons, and hazardous materials are strictly prohibited. The joint packing step ensures compliance.' },
];

export default function ProcessGuidePage() {
  const [activeStep, setActiveStep] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#0D2137] to-[#0F2A1A]">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#16A34A]/10 blur-[120px] translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#0D9488]/10 blur-[100px] -translate-x-1/4 translate-y-1/4" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#16A34A]/20 border border-[#16A34A]/30 text-[#4ADE80] text-sm font-semibold mb-6">
            <span>📋</span>
            <span>How MuyuRide Works</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            Send anything.<br />
            <span className="text-[#4ADE80]">Track everything.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            From posting your order to receiving payment — here's the complete MuyuRide workflow with real examples from Kenya's most-used delivery routes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link href="/sign-up-login-screen" className="px-6 py-3 rounded-xl bg-[#16A34A] text-white font-bold hover:bg-[#15803D] transition-colors shadow-lg shadow-green-900/30">
              Start Sending →
            </Link>
            <Link href="/sign-up-login-screen" className="px-6 py-3 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors">
              Become a Traveler
            </Link>
          </div>
        </div>
      </section>
      {/* Step navigator */}
      <section className="sticky top-16 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-none gap-1 py-3">
            {steps?.map((step, i) => (
              <button
                key={step?.number}
                onClick={() => setActiveStep(i)}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeStep === i
                    ? 'bg-[#16A34A] text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span>{step?.icon}</span>
                <span className="hidden sm:inline">{step?.phase}</span>
                <span className="sm:hidden">{step?.number}</span>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* Steps detail */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {steps?.map((step, i) => (
            <div
              key={step?.number}
              id={`step-${i}`}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
                activeStep === i ? 'shadow-xl ring-2 ring-offset-2' : 'shadow-md'
              } ${step?.border} bg-white`}
              style={activeStep === i ? { ringColor: step?.accent } : {}}
              onClick={() => setActiveStep(i)}
            >
              {/* Step header */}
              <div className={`bg-gradient-to-r ${step?.color} p-6 sm:p-8`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">
                    {step?.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="text-white/70 text-sm font-mono font-bold">Step {step?.number}</span>
                      <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-semibold">{step?.who}</span>
                      <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-semibold">⏱ {step?.time}</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white">{step?.title}</h2>
                    <p className="text-white/80 mt-2 text-base max-w-2xl">{step?.description}</p>
                  </div>
                </div>
              </div>

              {/* Step body */}
              <div className="p-6 sm:p-8 grid sm:grid-cols-2 gap-8">
                {/* Checklist */}
                <div>
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">What Happens</h3>
                  <ol className="space-y-3">
                    {step?.steps?.map((s, si) => (
                      <li key={si} className="flex items-start gap-3">
                        <span
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5"
                          style={{ background: step?.accent }}
                        >
                          {si + 1}
                        </span>
                        <span className="text-sm text-slate-700 leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Example + tip */}
                <div className="space-y-4">
                  <div className={`rounded-xl p-4 ${step?.lightBg} ${step?.border} border`}>
                    <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: step?.accent }}>
                      🇰🇪 {step?.example?.label}
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed italic">"{step?.example?.text}"</p>
                  </div>
                  <div className="rounded-xl p-4 bg-amber-50 border border-amber-200">
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">💡 Pro Tip</p>
                    <p className="text-sm text-amber-800 leading-relaxed">{step?.tip}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* Trust & Safety */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">Built on Trust</h2>
            <p className="text-slate-600 max-w-xl mx-auto">Every step of the MuyuRide process is designed to protect both senders and travelers.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trustFeatures?.map((f, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:shadow-md transition-shadow">
                <span className="text-3xl flex-shrink-0">{f?.icon}</span>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{f?.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f?.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Stats bar */}
      <section className="py-12 bg-gradient-to-r from-[#0F172A] to-[#0F2A1A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '31,400+', label: 'Deliveries Completed' },
              { value: '0', label: 'Parcels Lost' },
              { value: '98%', label: 'On-Time Rate' },
              { value: '4,820+', label: 'Verified Travelers' },
            ]?.map((stat, i) => (
              <div key={i}>
                <p className="text-3xl font-extrabold text-[#4ADE80]">{stat?.value}</p>
                <p className="text-sm text-slate-400 mt-1">{stat?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-slate-900 text-center mb-10">Common Questions</h2>
          <div className="space-y-3">
            {faqs?.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-semibold text-slate-900 text-sm sm:text-base">{faq?.q}</span>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center transition-transform ${openFaq === i ? 'rotate-180' : ''}`}>
                    <svg className="w-3.5 h-3.5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-slate-600 leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-[#16A34A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AppLogo size={56} className="justify-center mb-4" />
          <h2 className="text-3xl font-extrabold text-white mb-4">Ready to send your first parcel?</h2>
          <p className="text-green-100 mb-8">Join 31,400+ Kenyans who trust MuyuRide for affordable, safe, peer-to-peer delivery.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/sign-up-login-screen" className="px-8 py-3 rounded-xl bg-white text-[#16A34A] font-bold hover:bg-green-50 transition-colors shadow-lg">
              Get Started Free →
            </Link>
            <Link href="/about" className="px-8 py-3 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">
              About MuyuRide
            </Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
